"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

const scrollColumnClassName = cn(
  "h-[200px] w-10 overflow-y-auto overscroll-contain",
  "[scrollbar-width:none]",
  "[&::-webkit-scrollbar]:hidden",
);

function TimeScrollColumn({
  items,
  value,
  onSelect,
  "aria-label": ariaLabel,
}: {
  items: string[];
  value: string;
  onSelect: (value: string) => void;
  "aria-label": string;
}) {
  const selectedRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "center" });
  }, [value]);

  return (
    <div role="listbox" aria-label={ariaLabel} className={scrollColumnClassName}>
      {items.map((item) => (
        <button
          key={item}
          ref={item === value ? selectedRef : undefined}
          type="button"
          role="option"
          aria-selected={item === value}
          onClick={() => onSelect(item)}
          className={cn(
            "flex h-8 w-10 shrink-0 items-center justify-center body-sm-regular tabular-nums",
            item === value
              ? "bg-brand-50 text-brand-700 font-medium"
              : "text-text-primary hover:bg-surface-secondary",
          )}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function parseTimeValue(value: string | undefined): { hour: string; minute: string } {
  const match = /^(\d{2}):(\d{2})$/.exec(value?.trim() ?? "");
  if (!match) return { hour: "09", minute: "00" };
  return { hour: match[1], minute: match[2] };
}

export interface TimePickerPanelProps {
  value: string;
  onChange: (time: string) => void;
  className?: string;
}

export function TimePickerPanel({ value, onChange, className }: TimePickerPanelProps) {
  const { hour, minute } = parseTimeValue(value);

  const update = (nextHour: string, nextMinute: string) => {
    onChange(`${nextHour}:${nextMinute}`);
  };

  return (
    <div className={cn("inline-flex gap-1 p-1 bg-white", className)}>
      <TimeScrollColumn
        items={HOURS}
        value={hour}
        aria-label="Hours"
        onSelect={(nextHour) => update(nextHour, minute)}
      />
      <TimeScrollColumn
        items={MINUTES}
        value={minute}
        aria-label="Minutes"
        onSelect={(nextMinute) => update(hour, nextMinute)}
      />
    </div>
  );
}
